package com.snakesworld.game.ui.theme

import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Typography
import androidx.compose.material3.darkColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import org.jetbrains.compose.resources.Font
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.zcool_kuaile_regular

enum class JoystickType {
    Pad,
    Joystick
}

data class GameCanvasColors(
    val gridColor: Color,
    val backgroundColor: Color,
    val boundaryColor: Color,
    val dangerColor: Color,
    val hudTextColor: Color,
    val hudBgColor: Color,
    val accentColor: Color,
)

val DarkCanvasColors = GameCanvasColors(
    gridColor = Color(0xFF1E293B),
    backgroundColor = Color(0xFF0B0E14),
    boundaryColor = Color(0xFF00F0FF),
    dangerColor = Color(0xFFFF0055),
    hudTextColor = Color(0xFF00FF66),
    hudBgColor = Color(0xDD0D1117),
    accentColor = Color(0xFFFFEA00),
)

val LocalGameCanvasColors = staticCompositionLocalOf { DarkCanvasColors }
val LocalJoystickType = compositionLocalOf<MutableState<JoystickType>> { mutableStateOf(JoystickType.Joystick) }
val LocalAppFontFamily = staticCompositionLocalOf<FontFamily> { FontFamily.Default }

@Composable
fun appFontFamily(): FontFamily = LocalAppFontFamily.current

private val DarkColorScheme = darkColorScheme(
    primary = Color(0xFF00FF66),
    onPrimary = Color(0xFF00220D),
    secondary = Color(0xFF00F0FF),
    onSecondary = Color(0xFF002124),
    tertiary = Color(0xFFFF007F),
    background = Color(0xFF0B0E14),
    onBackground = Color(0xFFE2E8F0),
    surface = Color(0xFF131823),
    onSurface = Color(0xFFF1F5F9),
    surfaceVariant = Color(0xFF1E293B),
    onSurfaceVariant = Color(0xFF94A3B8),
)

@Composable
fun GameTheme(
    content: @Composable () -> Unit,
) {
    val canvasColors = DarkCanvasColors
    val colorScheme = DarkColorScheme

    val zcoolFont = Font(Res.font.zcool_kuaile_regular)
    val zcoolFamily = FontFamily(zcoolFont)

    val defaultTypography = Typography()
    val typography = Typography(
        displayLarge = defaultTypography.displayLarge.copy(fontFamily = zcoolFamily),
        displayMedium = defaultTypography.displayMedium.copy(fontFamily = zcoolFamily),
        displaySmall = defaultTypography.displaySmall.copy(fontFamily = zcoolFamily),
        headlineLarge = defaultTypography.headlineLarge.copy(fontFamily = zcoolFamily),
        headlineMedium = defaultTypography.headlineMedium.copy(fontFamily = zcoolFamily),
        headlineSmall = defaultTypography.headlineSmall.copy(fontFamily = zcoolFamily),
        titleLarge = defaultTypography.titleLarge.copy(fontFamily = zcoolFamily),
        titleMedium = defaultTypography.titleMedium.copy(fontFamily = zcoolFamily),
        titleSmall = defaultTypography.titleSmall.copy(fontFamily = zcoolFamily),
        bodyLarge = defaultTypography.bodyLarge.copy(fontFamily = zcoolFamily),
        bodyMedium = defaultTypography.bodyMedium.copy(fontFamily = zcoolFamily),
        bodySmall = defaultTypography.bodySmall.copy(fontFamily = zcoolFamily),
        labelLarge = defaultTypography.labelLarge.copy(fontFamily = zcoolFamily),
        labelMedium = defaultTypography.labelMedium.copy(fontFamily = zcoolFamily),
        labelSmall = defaultTypography.labelSmall.copy(fontFamily = zcoolFamily),
    )

    CompositionLocalProvider(
        LocalGameCanvasColors provides canvasColors,
        LocalAppFontFamily provides zcoolFamily,
    ) {
        MaterialTheme(
            colorScheme = colorScheme,
            typography = typography,
            content = content
        )
    }
}
